'use client';

import { useMemo } from 'react';
import { useReadContracts } from 'wagmi';
import { FARMS_CONFIG } from '@/constants/project-specific/farms/pools';
import { FARM_PAGE_CONFIG } from '@/constants/project-specific/farms';
import { getMasterChefConfigBySlug } from '@/util/contracts/masterchef';
import { chainSettings, ChainType, fallbackChainId } from '@/constants/chain-specific';
import { tokenAbi } from '@/constants/abi';
import { chewyLpAbi } from '@/constants/abi/chewyLpAbi';
import { getFarmAllocation } from '@/lib/farms/helpers';
import { getTokenAmount } from '@/util/bigNumber';
import BN from 'bignumber.js';
import type { ChainFarmData, FarmConfig, FarmRewardPrices, NativeTokenPrices } from '@/constants/project-specific/farms/types';
import type { RequiredFarmChainType } from '@/constants/project-specific';
import type { Address } from 'viem';
interface UseOptimizedFarmDataProps {
  prices: FarmRewardPrices;
  nativePrices: NativeTokenPrices;
  chainIds?: RequiredFarmChainType[];
  enabled?: boolean;
}
interface FarmContractCall {
  farmIndex: number;
  farmConfig: FarmConfig;
  chainId: RequiredFarmChainType;
  mcConfig: any;
  callType: 'tokenBalance' | 'quoteBalance' | 'lpBalance' | 'totalSupply' | 'poolInfo' | 'totalAlloc' | 'rewardPerBlock';
}
const FIXED_ZERO = new BN(0);

/**
 * Optimized farm data loading hook using wagmi's useReadContracts with multicall batching
 * Reduces RPC calls from hundreds to just a few batched multicalls
 */
export function useOptimizedFarmData({
  prices,
  nativePrices,
  chainIds = FARM_PAGE_CONFIG.chainIds,
  enabled = true
}: UseOptimizedFarmDataProps) {
  // Generate all contract calls upfront for efficient batching
  const contractCalls = useMemo<{
    calls: any[];
    farmMeta: FarmContractCall[];
  }>(() => {
    if (!enabled) return {
      calls: [],
      farmMeta: []
    };
    const calls: any[] = [];
    const farmMeta: FarmContractCall[] = [];
    let farmIndex = 0;
    for (const chainId of chainIds) {
      const farms = FARMS_CONFIG[chainId] || [];
      for (const farmConfig of farms) {
        if (farmConfig.display === false) continue;
        const mcConfig = getMasterChefConfigBySlug(farmConfig.masterChefSlug, chainId) ?? FARM_PAGE_CONFIG.defaultChef[fallbackChainId];
        if (!mcConfig) continue;
        const isTokenOnly = farmConfig.tokenB === undefined;

        // Token balance in LP
        calls.push({
          address: farmConfig.tokenA.address,
          abi: tokenAbi,
          functionName: 'balanceOf',
          args: [farmConfig.lpAddress],
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'tokenBalance'
        });

        // Quote token balance in LP (use tokenB if not tokenOnly, otherwise quoteToken)
        const quoteAddress = !isTokenOnly ? farmConfig.tokenB!.address : farmConfig.quoteToken.address;
        calls.push({
          address: quoteAddress,
          abi: chewyLpAbi,
          functionName: 'balanceOf',
          args: [farmConfig.lpAddress],
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'quoteBalance'
        });

        // LP token balance in MasterChef (or token balance for single token staking)
        const stakingAddress = isTokenOnly ? farmConfig.tokenA.address : farmConfig.lpAddress;
        calls.push({
          address: stakingAddress,
          abi: chewyLpAbi,
          functionName: 'balanceOf',
          args: [mcConfig.address],
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'lpBalance'
        });

        // LP total supply
        calls.push({
          address: farmConfig.lpAddress,
          abi: chewyLpAbi,
          functionName: 'totalSupply',
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'totalSupply'
        });

        // Pool info from MasterChef
        calls.push({
          address: mcConfig.address,
          abi: mcConfig.abi,
          functionName: 'poolInfo',
          args: [BigInt(farmConfig.pid)],
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'poolInfo'
        });

        // Total allocation points
        calls.push({
          address: mcConfig.address,
          abi: mcConfig.abi,
          functionName: 'totalAllocPoint',
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'totalAlloc'
        });

        // Reward per block
        calls.push({
          address: mcConfig.address,
          abi: mcConfig.abi,
          functionName: mcConfig.rewardPerBlockFunction,
          chainId
        });
        farmMeta.push({
          farmIndex,
          farmConfig,
          chainId,
          mcConfig,
          callType: 'rewardPerBlock'
        });
        farmIndex++;
      }
    }
    console.log(`📊 Generated ${calls.length} contract calls for ${farmIndex} farms across ${chainIds.length} chains`);
    return {
      calls,
      farmMeta
    };
  }, [chainIds, enabled]);

  // Use wagmi's useReadContracts with optimized batching
  const {
    data: contractResults,
    isLoading,
    error,
    refetch
  } = useReadContracts({
    contracts: contractCalls.calls,
    allowFailure: true,
    // Allow individual calls to fail without breaking the batch
    batchSize: 1024,
    // Optimize batch size for RPC limits
    query: {
      enabled: enabled && contractCalls.calls.length > 0,
      staleTime: 30 * 1000,
      // Cache for 30 seconds
      gcTime: 5 * 60 * 1000,
      // Keep in memory for 5 minutes
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000)
    },
    scopeKey: `farms-${chainIds.join('-')}` // Scope cache by chain IDs
  });

  // Process results into farm data structure
  const processedData = useMemo(() => {
    if (!contractResults || !contractCalls.farmMeta.length) {
      return null;
    }
    console.log(`🔄 Processing ${contractResults.length} contract results...`);
    const chainData: ChainFarmData = {
      [109]: [],
      [2000]: []
    };

    // Group results by farm
    const farmResults: {
      [key: number]: any[];
    } = {};
    contractCalls.farmMeta.forEach((meta, index) => {
      if (!farmResults[meta.farmIndex]) {
        farmResults[meta.farmIndex] = [];
      }
      const result = contractResults[index];
      if (result.status === 'success') {
        farmResults[meta.farmIndex].push({
          type: meta.callType,
          data: result.result,
          meta
        });
      } else {
        console.warn(`⚠️ Failed call for farm ${meta.farmIndex}, type ${meta.callType}:`, result.error);
        farmResults[meta.farmIndex].push({
          type: meta.callType,
          data: null,
          meta
        });
      }
    });

    // Process each farm's data
    Object.entries(farmResults).forEach(([farmIndexStr, results]) => {
      const farmIndex_0 = parseInt(farmIndexStr);

      // Get farm metadata from first result
      const firstResult = results[0];
      if (!firstResult) return;
      const {
        farmConfig: farmConfig_0,
        chainId: chainId_0,
        mcConfig: mcConfig_0
      } = firstResult.meta;
      const isTokenOnly_0 = farmConfig_0.tokenB === undefined;

      // Extract results by type
      const tokenBalance = results.find(r => r.type === 'tokenBalance')?.data as bigint || 0n;
      const quoteBalance = results.find(r_0 => r_0.type === 'quoteBalance')?.data as bigint || 0n;
      const lpBalance = results.find(r_1 => r_1.type === 'lpBalance')?.data as bigint || 0n;
      const totalSupply = results.find(r_2 => r_2.type === 'totalSupply')?.data as bigint || 0n;
      const poolInfo = results.find(r_3 => r_3.type === 'poolInfo')?.data as any[];
      const totalAllocPoint = results.find(r_4 => r_4.type === 'totalAlloc')?.data as bigint || 0n;
      const rewardPerBlock = results.find(r_5 => r_5.type === 'rewardPerBlock')?.data as bigint || 0n;

      // Skip if essential data is missing
      if (!poolInfo || !totalAllocPoint) {
        console.warn(`⚠️ Skipping farm ${farmConfig_0.tokenA.symbol} due to missing essential data`);
        return;
      }
      try {
        // Calculate LP token ratios and values (similar to original logic)
        let tokenAmount: BN = new BN(0);
        let lpTotalInQuoteToken: BN | undefined;
        let tokenPriceVsQuote: BN;
        let lpTokenRatio: BN;
        let tokensPerLP: BN = new BN(0);
        let quoteTokensPerLP: BN = new BN(0);
        let quoteTokenAmount: BN = new BN(0);
        let farmTotalValue: BN = new BN(0);
        if (isTokenOnly_0) {
          tokenAmount = getTokenAmount(new BN(lpBalance.toString()), farmConfig_0.tokenA.decimals);
          tokenPriceVsQuote = new BN(quoteBalance.toString()).div(new BN(tokenBalance.toString()));
          if (farmConfig_0.tokenA.decimals !== 18) {
            tokenPriceVsQuote = getTokenAmount(new BN(quoteBalance.toString()).div(new BN(tokenBalance.toString())), farmConfig_0.tokenA.decimals);
          }
          lpTotalInQuoteToken = tokenAmount.times(tokenPriceVsQuote);
        } else {
          lpTokenRatio = new BN(lpBalance.toString()).div(new BN(totalSupply.toString()));
          tokensPerLP = new BN(tokenBalance.toString()).div(new BN(totalSupply.toString()));
          quoteTokensPerLP = new BN(quoteBalance.toString()).div(new BN(totalSupply.toString()));
          lpTotalInQuoteToken = getTokenAmount(new BN(quoteBalance.toString()), farmConfig_0.quoteToken.decimals).times(new BN(2)).times(lpTokenRatio);
          tokenAmount = new BN(tokenBalance.toString()).times(lpTokenRatio);
          quoteTokenAmount = getTokenAmount(new BN(quoteBalance.toString()), farmConfig_0.quoteToken.decimals).times(lpTokenRatio);
          if (tokenAmount.gt(0)) {
            tokenPriceVsQuote = quoteTokenAmount.div(tokenAmount);
          } else {
            tokenPriceVsQuote = new BN(quoteBalance.toString()).div(new BN(tokenBalance.toString()));
          }
        }

        // Calculate farm metrics
        const totalAllocPointBN = new BN(totalAllocPoint.toString());
        const rewardPerBlockBN = rewardPerBlock ? new BN(rewardPerBlock.toString()) : FIXED_ZERO;
        const allocPoint = new BN(poolInfo[1].toString());
        const {
          poolWeight
        } = getFarmAllocation({
          allocPoint: poolInfo[1],
          totalAllocPoint: totalAllocPoint
        });
        const poolRewardPerBlock = getTokenAmount(rewardPerBlockBN.times(poolWeight), mcConfig_0.rewardToken.decimals);
        const poolRewardPerYear = Number(poolRewardPerBlock) * chainSettings[chainId_0 as ChainType].blocksPerYear;
        const rewardPrice = new BN(prices[chainId_0]?.find(reward => reward.rewardContract === mcConfig_0.rewardToken.address.toLowerCase())?.rewardPrice ?? 0);
        const nativePrice = new BN(nativePrices[chainId_0]?.priceUsd ?? 0);
        let apy = new BN(rewardPrice.times(poolRewardPerYear));
        farmTotalValue = new BN(lpTotalInQuoteToken ?? 0);

        // Apply quote token pricing
        if (farmConfig_0.quoteToken?.address?.toLowerCase() === chainSettings[chainId_0 as ChainType]?.wNative?.address?.toLowerCase()) {
          farmTotalValue = farmTotalValue.times(nativePrice);
        } else if (!isTokenOnly_0) {
          const quotePrice = prices[chainId_0]?.find(reward_0 => reward_0.rewardContract === farmConfig_0.quoteToken.address.toLowerCase())?.rewardPrice ?? 0;
          farmTotalValue = farmTotalValue.times(new BN(String(quotePrice)));
        }

        // Calculate APY
        if (farmTotalValue.gt(0)) {
          apy = apy.div(farmTotalValue);
        }
        const processedFarm = {
          ...farmConfig_0,
          chainId: chainId_0,
          tokenAmount: Number(tokenAmount),
          quoteTokenAmount: Number(quoteTokenAmount),
          lpTotalInQuoteToken: Number(lpTotalInQuoteToken),
          tokenPriceVsQuote: Number(tokenPriceVsQuote),
          poolWeight: Number(poolWeight),
          depositFeeBP: poolInfo[4],
          rewardPerBlock: Number(poolRewardPerBlock),
          tokensPerLP: Number(tokensPerLP),
          quoteTokensPerLP: Number(quoteTokensPerLP),
          apy: Number(apy),
          totalValue: Number(farmTotalValue),
          rewardPrice: Number(rewardPrice),
          nativePrice: Number(nativePrice),
          isTokenOnly: isTokenOnly_0
        };
        chainData[chainId_0 as keyof ChainFarmData].push(processedFarm);
      } catch (error_0) {
        console.error(`❌ Error processing farm ${farmConfig_0.tokenA.symbol}:`, error_0);
      }
    });
    console.log(`✅ Processed farm data for chains:`, Object.keys(chainData).map(chainId_1 => `${chainId_1}: ${chainData[chainId_1 as unknown as keyof ChainFarmData].length} farms`).join(', '));
    return chainData;
  }, [contractResults, contractCalls.farmMeta, prices, nativePrices]);
  return {
    data: processedData,
    isLoading,
    error,
    refetch,
    // Expose some debug info
    totalCalls: contractCalls.calls.length
  };
}